import * as React from 'react';
import Box from '@mui/material/Box';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TablePagination from '@mui/material/TablePagination';
import TableRow from '@mui/material/TableRow';
import TableSortLabel from '@mui/material/TableSortLabel';
import Paper from '@mui/material/Paper';
import IconButton from '@mui/material/IconButton';
import Snackbar from '@mui/material/Snackbar';
import Alert from '@mui/material/Alert';
import SkeletonTableRows from './SkeletonTableRows';
import DoneIcon from '@mui/icons-material/Done';
import BlockIcon from '@mui/icons-material/Block';
import { visuallyHidden } from '@mui/utils';
import { useTranslation } from 'react-i18next';
import { useApproveRaceMutation, useGetPendingRacesQuery, useRejectRaceMutation } from '../api/apiSlice';
import { RaceRequest } from '../types/api';
import { useSportTypeLabel } from '../hooks/useSportTypeLabel';
import { Order, getComparator, stableSort } from '../utils/tableSort';
import { formatShortDate } from '../utils/formatDate';

const headCellsBase: { id: keyof RaceRequest; disablePadding: boolean; labelKey: string }[] = [
  {
    id: 'TITLE',
    disablePadding: true,
    labelKey: 'raceManagement.title',
  },
  {
    id: 'SPORT_TYPE',
    disablePadding: false,
    labelKey: 'raceManagement.sport',
  },
  {
    id: 'DATE',
    disablePadding: false,
    labelKey: 'raceManagement.date',
  },
  {
    id: 'LOCATION',
    disablePadding: false,
    labelKey: 'raceManagement.location',
  },
];

interface EnhancedTableHeadProps {
  order: Order;
  orderBy: string;
  rowCount: number;
  onRequestSort: (event: React.MouseEvent<unknown>, property: keyof RaceRequest) => void;
}

function EnhancedTableHead({ order, orderBy, onRequestSort }: EnhancedTableHeadProps) {
  const { t } = useTranslation();
  const createSortHandler = (property: keyof RaceRequest) => (event: React.MouseEvent<unknown>) => {
    onRequestSort(event, property);
  };

  return (
    <TableHead>
      <TableRow>
        {headCellsBase.map((headCell) => (
          <TableCell
            key={headCell.id}
            align={'center'}
            padding={headCell.disablePadding ? 'normal' : 'normal'}
            sortDirection={orderBy === headCell.id ? order : false}
          >
            <TableSortLabel
              active={orderBy === headCell.id}
              direction={orderBy === headCell.id ? order : 'asc'}
              onClick={createSortHandler(headCell.id)}
            >
              {t(headCell.labelKey as 'raceManagement.title')}
              {orderBy === headCell.id ? (
                <Box component="span" sx={visuallyHidden}>
                  {order === 'desc' ? t('common.sortedDescending') : t('common.sortedAscending')}
                </Box>
              ) : null}
            </TableSortLabel>
          </TableCell>
        ))}
      </TableRow>
    </TableHead>
  );
}

interface RaceRequestsProps {
  clubId?: number;
}

export default function RaceRequests({ clubId }: RaceRequestsProps) {
  const { t, i18n } = useTranslation();
  const sportTypeLabel = useSportTypeLabel();
  const [order, setOrder] = React.useState<Order>('asc');
  const [orderBy, setOrderBy] = React.useState<keyof RaceRequest>('TITLE');
  const [page, setPage] = React.useState(0);
  const [rowsPerPage, setRowsPerPage] = React.useState(5);
  const [errorSnackbarOpen, setErrorSnackbarOpen] = React.useState(false);
  const clubID = clubId != null ? String(clubId) : '';

  const { data: requests = [], isLoading } = useGetPendingRacesQuery(clubID, { skip: !clubID });
  const [approveRace] = useApproveRaceMutation();
  const [rejectRace] = useRejectRaceMutation();

  const handleRequestSort = (event: React.MouseEvent<unknown>, property: keyof RaceRequest) => {
    const isAsc = orderBy === property && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(property);
  };

  const handleChangePage = (event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleApproveRace = async (raceId: number) => {
    try {
      await approveRace(raceId).unwrap();
    } catch (error) {
      console.error('Error:', error);
      setErrorSnackbarOpen(true);
    }
  };

  const handleRejectRace = async (raceId: number) => {
    try {
      await rejectRace(raceId).unwrap();
    } catch (error) {
      console.error('Error:', error);
      setErrorSnackbarOpen(true);
    }
  };

  const emptyRows =
    page > 0 ? Math.max(0, (1 + page) * rowsPerPage - requests.length) : 0;

  const visibleRows = React.useMemo(
    () =>
      stableSort(requests, getComparator<RaceRequest, keyof RaceRequest>(order, orderBy)).slice(
        page * rowsPerPage,
        page * rowsPerPage + rowsPerPage,
      ),
    [order, orderBy, page, rowsPerPage, requests],
  );

  return (
    <Box sx={{ width: '100%' }}>
      <Paper sx={{ width: '100%', mb: 2 }}>
        <TableContainer sx={{ overflowX: 'auto', border: 1, borderColor: 'divider', borderRadius: 1 }}>
          <Table
            sx={{ minWidth: { xs: 450, sm: 750 } }}
            aria-labelledby="tableTitle"
            size={'medium'}
          >
            <EnhancedTableHead
              order={order}
              orderBy={orderBy}
              onRequestSort={handleRequestSort}
              rowCount={requests.length}
            />
            <TableBody>
              {isLoading && (
                <SkeletonTableRows
                  rows={3}
                  cells={[
                    {}, {}, {}, {},
                    { variant: 'circular', width: 24, height: 24, center: true },
                    { variant: 'circular', width: 24, height: 24, center: true },
                  ]}
                />
              )}

              {!isLoading && requests.length === 0 && (
                <TableRow>
                  <TableCell colSpan={6} align="center" sx={{ py: 4, color: 'text.secondary' }}>
                    {t('raceRequests.empty')}
                  </TableCell>
                </TableRow>
              )}

              {visibleRows.map((row, index) => {
              const labelId = `enhanced-table-checkbox-${index}`;

              return (
                <TableRow
                  key={row.ID}
                  hover
                  tabIndex={-1}
                  sx={{ cursor: 'pointer' }}
                >
                  <TableCell component="th" id={labelId} scope="row" padding="normal" align='center'>
                    {row.TITLE}
                  </TableCell>
                  <TableCell align="center">{sportTypeLabel(row.SPORT_TYPE)}</TableCell>
                  <TableCell align="center">
                    {formatShortDate(row.DATE, i18n.language)}
                  </TableCell>
                  <TableCell align="center">{row.LOCATION}</TableCell>

                  <TableCell align='center' component="th" scope="row">
                    <IconButton onClick={() => handleApproveRace(row.ID)} aria-label={`${t('common.approve')}: ${row.TITLE}`}>
                      <DoneIcon color='success'/>
                    </IconButton>
                  </TableCell>

                  <TableCell align='center' component="th" scope="row">
                    <IconButton onClick={() => handleRejectRace(row.ID)} aria-label={`${t('common.reject')}: ${row.TITLE}`}>
                      <BlockIcon color='error' />
                    </IconButton>
                  </TableCell>
                </TableRow>
              );
            })}
              {emptyRows > 0 && (
                <TableRow
                  style={{
                    height: (53) * emptyRows,
                  }}
                >
                  <TableCell colSpan={6} />
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
        <TablePagination
          rowsPerPageOptions={[5, 10, 25]}
          component="div"
          count={requests.length}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
        />
      </Paper>
      <Snackbar open={errorSnackbarOpen} autoHideDuration={4000} onClose={() => setErrorSnackbarOpen(false)} anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}>
        <Alert onClose={() => setErrorSnackbarOpen(false)} severity="error" variant="filled">
          {t('common.genericError')}
        </Alert>
      </Snackbar>
    </Box>
  );
}
